<script>
    import { onMount } from 'svelte';

    export let currentUser;

    let users = [];
    let loading = true;
    let error = '';
    let showModal = false;
    let editingUser = null;

    let formData = {
        name: '',
        email: '',
        password: '',
        role: 'agent',
        is_active: true
    };

    const roleLabels = {
        'super_admin': { label: 'Super Admin', color: '#9c27b0', icon: '👑' },
        'admin': { label: 'Administrador', color: '#2196f3', icon: '🔧' },
        'agent': { label: 'Agente', color: '#4caf50', icon: '💬' },
        'viewer': { label: 'Visor', color: '#757575', icon: '👁' }
    };

    onMount(loadUsers);

    async function loadUsers() {
        loading = true;
        try {
            const token = localStorage.getItem('token');

            // /api/users returns the requester's own org users (all orgs for a
            // super admin). Works for org admins too — the org-scoped endpoint
            // under /api/organizations/<id>/users is super-admin only.
            const res = await fetch('/api/users', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                users = await res.json();
            }
        } catch (e) {
            error = e.message;
        }
        loading = false;
    }

    function openAddModal() {
        editingUser = null;
        formData = { name: '', email: '', password: '', role: 'agent', is_active: true };
        showModal = true;
    }

    function openEditModal(user) {
        editingUser = user;
        formData = { ...user, password: '' };
        showModal = true;
    }

    async function saveUser() {
        try {
            const token = localStorage.getItem('token');
            const method = editingUser ? 'PUT' : 'POST';
            // Org-admin-scoped endpoints (backend derives the org from the JWT).
            const url = editingUser
                ? `/api/users/${editingUser.id}`
                : `/api/users`;

            const body = { ...formData };
            if (!body.password) delete body.password;

            const res = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Error saving user');
            }

            showModal = false;
            loadUsers();
        } catch (e) {
            error = e.message;
        }
    }

    async function toggleUserStatus(user) {
        try {
            const token = localStorage.getItem('token');

            await fetch(`/api/users/${user.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ is_active: !user.is_active })
            });

            loadUsers();
        } catch (e) {
            error = e.message;
        }
    }

    function canManageRole(role) {
        if (currentUser.role === 'super_admin') return role !== 'super_admin';
        if (currentUser.role === 'admin') return ['agent', 'viewer'].includes(role);
        return false;
    }

    function getAvailableRoles() {
        if (currentUser.role === 'super_admin') return ['admin', 'agent', 'viewer'];
        if (currentUser.role === 'admin') return ['agent', 'viewer'];
        return [];
    }
</script>

<style>
    .users-container {
        padding: 20px;
    }

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }

    h2 {
        margin: 0;
        color: #333;
    }

    .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
    }

    .btn-primary {
        background: #2196f3;
        color: white;
    }

    .users-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 16px;
    }

    .user-card {
        background: white;
        border-radius: 8px;
        padding: 16px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    .user-card.inactive {
        opacity: 0.6;
    }

    .user-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;
    }

    .avatar {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: #e0e0e0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
    }

    .user-info h3 {
        margin: 0;
        font-size: 16px;
    }

    .user-info small {
        color: #666;
    }

    .role-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
        color: white;
    }

    .user-actions {
        display: flex;
        gap: 8px;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid #eee;
    }

    .btn-small {
        padding: 6px 12px;
        font-size: 12px;
    }

    .btn-secondary {
        background: #757575;
        color: white;
    }

    .btn-danger {
        background: #f44336;
        color: white;
    }

    .btn-success {
        background: #4caf50;
        color: white;
    }

    /* Modal */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .modal {
        background: white;
        padding: 24px;
        border-radius: 8px;
        width: 400px;
        max-width: 90%;
    }

    .modal h3 {
        margin: 0 0 20px;
    }

    .form-group {
        margin-bottom: 16px;
    }

    .form-group label {
        display: block;
        margin-bottom: 4px;
        font-weight: 500;
    }

    .form-group input,
    .form-group select {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
    }

    .modal-actions {
        display: flex;
        gap: 10px;
        justify-content: flex-end;
        margin-top: 20px;
    }

    .error {
        background: #ffebee;
        color: #c62828;
        padding: 10px;
        border-radius: 4px;
        margin-bottom: 16px;
    }

    .loading {
        text-align: center;
        padding: 40px;
        color: #666;
    }
</style>

<div class="users-container">
    <div class="header">
        <h2>👥 Usuarios</h2>
        {#if currentUser.role === 'admin' || currentUser.role === 'super_admin'}
            <button class="btn btn-primary" on:click={openAddModal}>+ Nuevo Usuario</button>
        {/if}
    </div>

    {#if error}
        <div class="error">{error}</div>
    {/if}

    {#if loading}
        <div class="loading">Cargando usuarios...</div>
    {:else}
        <div class="users-grid">
            {#each users as user}
                <div class="user-card" class:inactive={!user.is_active}>
                    <div class="user-header">
                        <div class="avatar">
                            {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div class="user-info">
                            <h3>{user.name}</h3>
                            <small>{user.email}</small>
                        </div>
                    </div>

                    <span class="role-badge" style="background: {roleLabels[user.role]?.color || '#757575'}">
                        {roleLabels[user.role]?.icon} {roleLabels[user.role]?.label || user.role}
                    </span>

                    {#if !user.is_active}
                        <span class="role-badge" style="background: #f44336; margin-left: 8px;">Inactivo</span>
                    {/if}

                    {#if canManageRole(user.role) && user.id !== currentUser.id}
                        <div class="user-actions">
                            <button class="btn btn-small btn-secondary" on:click={() => openEditModal(user)}>
                                Editar
                            </button>
                            <button
                                class="btn btn-small {user.is_active ? 'btn-danger' : 'btn-success'}"
                                on:click={() => toggleUserStatus(user)}
                            >
                                {user.is_active ? 'Desactivar' : 'Activar'}
                            </button>
                        </div>
                    {/if}
                </div>
            {/each}
        </div>
    {/if}
</div>

{#if showModal}
    <div class="modal-overlay" on:click|self={() => showModal = false}>
        <div class="modal">
            <h3>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>

            <div class="form-group">
                <label>Nombre</label>
                <input type="text" bind:value={formData.name} />
            </div>

            <div class="form-group">
                <label>Email</label>
                <input type="email" bind:value={formData.email} disabled={!!editingUser} />
            </div>

            <div class="form-group">
                <label>Contrasena {editingUser ? '(dejar vacio para no cambiar)' : ''}</label>
                <input type="password" bind:value={formData.password} />
            </div>

            <div class="form-group">
                <label>Rol</label>
                <select bind:value={formData.role}>
                    {#each getAvailableRoles() as role}
                        <option value={role}>{roleLabels[role]?.label || role}</option>
                    {/each}
                </select>
            </div>

            <div class="modal-actions">
                <button class="btn btn-secondary" on:click={() => showModal = false}>Cancelar</button>
                <button class="btn btn-primary" on:click={saveUser}>Guardar</button>
            </div>
        </div>
    </div>
{/if}
